import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { getAdminSettingsList } from '../services/api-service';
import { cacheSlashcallSuperAdminPersonaApiKey } from '../services/auth-service';
import { checkRoles, sanitizeVocCommandCenterApiKey } from '../utils/util';

interface AdminSetting {
    ParameterID?: number;
    ParameterCode: string;
    ParameterName?: string;
    ParameterValue: string | boolean | null;
    DefaultValue: string | boolean;
    InputType?: string;
}

interface AdminSettingsResponse {
    Status: string;
    Data: AdminSetting[];
}

type LoadingState = 'idle' | 'loading' | 'success' | 'error';

interface SettingsContextType {
    settings: AdminSetting[];
    loadingState: LoadingState;
    error: Error | null;
    getSettingValue: (parameterCode: string) => string | boolean | null;
    getSettingValueAsBoolean: (parameterCode: string) => boolean;
    getSettingValueAsString: (parameterCode: string) => string;
    getSettingValueAsNumber: (parameterCode: string) => number | null;
    getSetting: (parameterCode: string) => AdminSetting | undefined;
    refreshSettings: () => Promise<void>;
    isLoading: boolean;
    isLoaded: boolean;
    hasError: boolean;
}

interface SettingsProviderProps {
    children: ReactNode;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);


export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
    const [settings, setSettings] = useState<AdminSetting[]>([]);
    const [loadingState, setLoadingState] = useState<LoadingState>('idle');
    const [error, setError] = useState<Error | null>(null);

    const fetchSettings = useCallback(async () => {
        setLoadingState('loading');
        setError(null);
        try {
            const response: AdminSettingsResponse | any = await getAdminSettingsList();
            if (response?.Data && Array.isArray(response.Data)) {
                setSettings(response.Data);
                setLoadingState('success');
                try {
                    const vocSetting = response.Data.find((setting: AdminSetting) => setting.ParameterCode === 'VOC_COMMAND_CENTER');
                    if (vocSetting) {
                        const rawValue = vocSetting.ParameterValue !== null ? vocSetting.ParameterValue : vocSetting.DefaultValue;
                        cacheSlashcallSuperAdminPersonaApiKey(
                            sanitizeVocCommandCenterApiKey(rawValue),
                            checkRoles('ROLE_SLASHCALL')
                        );
                    }
                } catch (err) {
                    console.error('Error caching slashcall super admin persona api key:', err);
                }
            } else {
                throw new Error('Invalid response format from settings API');
            }
        } catch (err) {
            console.error('Failed to fetch admin settings:', err);
            setError(err instanceof Error ? err : new Error('Failed to fetch settings'));
            setLoadingState('error');
            setSettings([]);
        }
    }, []);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const getSettingValue = useCallback((parameterCode: string): string | boolean | null => {
        try {
            const setting = settings.find(s => s.ParameterCode === parameterCode);
            if (!setting) return null;
            return setting.ParameterValue !== null ? setting.ParameterValue : setting.DefaultValue;
        } catch (error) {
            console.error(`Error getting setting value for ${parameterCode}:`, error);
            return null;
        }
    }, [settings]);

    const getSettingValueAsString = useCallback((parameterCode: string): string => {
        try {
            const value = getSettingValue(parameterCode);
            if (value === null) return '';
            return String(value);
        } catch (error) {
            console.error(`Error getting setting value as string for ${parameterCode}:`, error);
            return '';
        }
    }, [getSettingValue]);

    const getSettingValueAsNumber = useCallback((parameterCode: string): number | null => {
        try {
            const value = getSettingValue(parameterCode);
            if (value === null) return null;
            const num = Number(value);
            return isNaN(num) ? null : num;
        } catch (error) {
            console.error(`Error getting setting value as number for ${parameterCode}:`, error);
            return null;
        }
    }, [getSettingValue]);

    const getSetting = useCallback((parameterCode: string): AdminSetting | undefined => {
        try {
            return settings.find(s => s.ParameterCode === parameterCode);
        } catch (error) {
            console.error(`Error getting setting for ${parameterCode}:`, error);
            return undefined;
        }
    }, [settings]);

    const getSettingValueAsBoolean = useCallback((parameterCode: string): boolean => {
        try {
            const data = getSetting(parameterCode);
            if (data?.ParameterValue !== null) {
                if (data?.ParameterValue === "true" || data?.ParameterValue === true) {
                    return true;
                } else if (data?.ParameterValue === "false" || data?.ParameterValue === false) {
                    return false;
                }
            } else if (data?.ParameterValue === null) {
                if (data?.DefaultValue === "true" || data?.DefaultValue === true) {
                    return true;
                } else if (data?.DefaultValue === "false" || data?.DefaultValue === false) {
                    return false;
                }
            }
            return false;
        } catch (error) {
            console.error(`Error getting setting value as boolean for ${parameterCode}:`, error);
            return false;
        }
    }, [getSetting]);

    const refreshSettings = useCallback(async () => {
        await fetchSettings();
    }, [fetchSettings]);

    const isLoading = loadingState === 'loading';
    const isLoaded = loadingState === 'success';
    const hasError = loadingState === 'error';

    const contextValue: SettingsContextType = {
        settings,
        loadingState,
        error,
        getSettingValue,
        getSettingValueAsBoolean,
        getSettingValueAsString,
        getSettingValueAsNumber,
        getSetting,
        refreshSettings,
        isLoading,
        isLoaded,
        hasError,
    };

    return (
        <SettingsContext.Provider value={contextValue}>
            {children}
        </SettingsContext.Provider>
    );
};


export const useSettings = (): SettingsContextType => {
    const context = useContext(SettingsContext);
    if (context === undefined) {
        throw new Error('useSettings must be used within a SettingsProvider');
    }
    return context;
};

export const useSettingsReady = (): boolean => {
    const { isLoaded } = useSettings();
    return isLoaded;
};

export const useCheckBooleanSetting = (settingCode: string): boolean => {
    const { isLoaded, getSettingValueAsBoolean } = useSettings();
    const [value, setValue] = useState<boolean>(false);

    useEffect(() => {
        if (isLoaded && settingCode) {
            const settingValue = getSettingValueAsBoolean(settingCode);
            setValue(settingValue);
        }
    }, [isLoaded, settingCode, getSettingValueAsBoolean]);

    return value;
};

export const useSettingValueByCode = (settingCode: string): string => {
    const { isLoaded, getSettingValueAsString } = useSettings();
    const [setting, setSetting] = useState<string>('');

    useEffect(() => {
        if (isLoaded && settingCode) {
            const settingValue = getSettingValueAsString(settingCode);
            setSetting(settingValue);
        }
    }, [isLoaded, settingCode, getSettingValueAsString]);

    return setting;
};

export default SettingsContext;
